import type { PolicyModel } from '@snowit/types/policies';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import { sortModel } from '@snowit/utilities/misc';
import omitter from '@snowit/utilities/omitter';

import useSchemas from '../useSchemas';

export default () => {
  const firebase = useFirebase();
  const { writeDocument, deleteDocument } = useSchemas();
  const { setStore, store } = useStore();

  const getPolicies = async (): Promise<PolicyModel[]> => {
    try {
      const list = await firebase.query<PolicyModel>({
        collectionName: 'policies',
        filters: (q) => q.limit(0),
      });
      setStore('policies', {
        list,
      });
      return list;
    } catch (error) {
      console.error({ error });
      setStore('policies', { list: [] });
      return [];
    } finally {
      setStore('policies', { ready: true });
    }
  };

  const getPolicy = async (id: string): Promise<PolicyModel | undefined> => {
    try {
      const doc = await firebase.doc<PolicyModel>({
        collectionName: 'policies',
        id,
      });
      if (!doc) return undefined;
      return doc;
    } catch (error) {
      console.error('policies_get_single', { error });
      return undefined;
    }
  };

  const writePolicy = async (uid: string | undefined, data: Partial<PolicyModel>) => {
    try {
      return await writeDocument({
        modelName: SchemaNames.POLICY,
        attributes: omitter(data),
        id: isNewEntity(uid) ? undefined : uid,
      });
    } catch (e: any) {
      console.error(e);
      return undefined;
    }
  };

  const deletePolicy = async (policyUID: string): Promise<boolean> => {
    try {
      if (isNewEntity(policyUID)) return false;
      await deleteDocument({
        modelName: SchemaNames.POLICY,
        id: policyUID,
      });
      return true;
    } catch (error: any) {
      console.error('deletePolicy', { error });
      return false;
    }
  };

  return {
    policies: store.policies.list.sort(sortModel),
    ready: Boolean(store.policies.ready),
    writePolicy,
    getPolicies,
    getPolicy,
    deletePolicy,
  };
};
